import { isEqual } from 'lodash-es';

import { insertInArrayByIndex } from '@editor2/services/helpersService';
import { indentElements } from './elementsAdapters';
import { type CourseState } from '@editor2/types/courseState';
import { ChangesStatus } from '@editor2/types/changesStatus';
import { pageToPageProperties } from '@editor2/mappers/page';

import {
	addStatus,
	deleteProperty,
	getChapterPages,
	isEqualIdsArrays,
	removeStatus,
	renumberChapterPages
} from './helpers';
import {
	getChapterByPageGroupId,
	getChapterByPageId
} from '@editor2/components/CourseChangesDiff/helpers';

export const addPageToPageGroupHelper = (state, payload) => {
	const { pageGroupId, pageEntity, index } = payload;
	const pageGroupPages = state.page_groups.entities[pageGroupId].pages;
	const ind = index === undefined ? pageGroupPages.length : index;

	const pagesIds = insertInArrayByIndex(pageGroupPages, pageEntity.family_id, ind);

	state.pages.entities[pageEntity.family_id] = pageEntity;
	state.pages.ids.push(pageEntity.family_id);

	state.page_groups.entities[pageGroupId].pages = pagesIds;

	state.changes.pages[pageEntity.family_id] = [ChangesStatus.New];
	state.changes.page_groups[pageGroupId] = addStatus(
		state.changes.page_groups[pageGroupId],
		ChangesStatus.ChildrenReorder
	);

	const parentChapter = getChapterByPageGroupId(state.chapters, pageGroupId);
	renumberChapterPages(state, parentChapter);

	return state;
};

export const addPageToChapterHelper = (state, payload) => {
	const { chapterId, pageEntity, index } = payload;
	const chapterPages = state.chapters.entities[chapterId].pages;
	const ind = index === undefined ? chapterPages.length : index;

	const pagesIds = insertInArrayByIndex(chapterPages, pageEntity.family_id, ind);

	state.pages.entities[pageEntity.family_id] = pageEntity;
	state.pages.ids.push(pageEntity.family_id);

	state.chapters.entities[chapterId].pages = pagesIds;

	state.changes.pages[pageEntity.family_id] = [ChangesStatus.New];
	state.changes.chapters[chapterId] = addStatus(
		state.changes.chapters[chapterId],
		ChangesStatus.ChildrenReorder
	);

	renumberChapterPages(state, state.chapters.entities[chapterId]);

	return state;
};

// eslint-disable-next-line max-statements
export const movePageHelper = (state, payload) => {
	const { sourceDroppableId, destinationDroppableId, destinationIndex, pageId } = payload;

	// delete from source droppable: chapter or page group
	if (state.page_groups.entities[sourceDroppableId]) {
		const initSourcePageGroup = state.initialCourse.page_groups.entities[sourceDroppableId];
		const sourcePageGroup = state.page_groups.entities[sourceDroppableId];
		const pageGroupEntity = {
			...sourcePageGroup,
			pages: sourcePageGroup.pages.filter((id) => id !== pageId)
		};

		const isSourceEqual = isEqualIdsArrays(initSourcePageGroup?.pages || [], pageGroupEntity.pages);

		state.page_groups.entities[sourceDroppableId] = pageGroupEntity;
		state.changes.page_groups[sourceDroppableId] = isSourceEqual
			? removeStatus(state.changes.page_groups[sourceDroppableId], ChangesStatus.ChildrenReorder)
			: addStatus(state.changes.page_groups[sourceDroppableId], ChangesStatus.ChildrenReorder);

		const parentChapter = getChapterByPageGroupId(state.chapters, sourceDroppableId);
		renumberChapterPages(state, parentChapter);
	}

	if (state.chapters.entities[sourceDroppableId]) {
		const initSourceChapter = state.initialCourse.chapters.entities[sourceDroppableId];
		const sourceChapter = state.chapters.entities[sourceDroppableId];
		const chapterEntity = {
			...sourceChapter,
			pages: sourceChapter.pages.filter((id) => id !== pageId)
		};

		const isSourceEqual = isEqualIdsArrays(initSourceChapter?.pages || [], chapterEntity.pages);

		state.chapters.entities[sourceDroppableId] = chapterEntity;
		state.changes.chapters[sourceDroppableId] = isSourceEqual
			? removeStatus(state.changes.chapters[sourceDroppableId], ChangesStatus.ChildrenReorder)
			: addStatus(state.changes.chapters[sourceDroppableId], ChangesStatus.ChildrenReorder);

		renumberChapterPages(state, state.chapters.entities[sourceDroppableId]);
	}

	// add to destination droppable: chapter or page group
	if (state.page_groups.entities[destinationDroppableId]) {
		const initDestinationPageGroup =
			state.initialCourse.page_groups.entities[destinationDroppableId];
		const destinationPageGroup = state.page_groups.entities[destinationDroppableId];
		const pageGroupEntity = {
			...destinationPageGroup,
			pages: insertInArrayByIndex(destinationPageGroup.pages, pageId, destinationIndex)
		};

		state.page_groups.entities[destinationDroppableId] = pageGroupEntity;
		state.changes.page_groups[destinationDroppableId] = isEqualIdsArrays(
			initDestinationPageGroup?.pages || [],
			pageGroupEntity.pages
		)
			? removeStatus(
					state.changes.page_groups[destinationDroppableId],
					ChangesStatus.ChildrenReorder
			  )
			: addStatus(state.changes.page_groups[destinationDroppableId], ChangesStatus.ChildrenReorder);

		const parentChapter = getChapterByPageGroupId(state.chapters, destinationDroppableId);
		renumberChapterPages(state, parentChapter);
	}

	if (state.chapters.entities[destinationDroppableId]) {
		const initDestinationChapter = state.initialCourse.chapters.entities[destinationDroppableId];
		const destinationChapter = state.chapters.entities[destinationDroppableId];
		const chapterEntity = {
			...destinationChapter,
			pages: insertInArrayByIndex(destinationChapter.pages, pageId, destinationIndex)
		};

		state.chapters.entities[destinationDroppableId] = chapterEntity;
		state.changes.chapters[destinationDroppableId] = isEqualIdsArrays(
			initDestinationChapter?.pages || [],
			chapterEntity.pages
		)
			? removeStatus(state.changes.chapters[destinationDroppableId], ChangesStatus.ChildrenReorder)
			: addStatus(state.changes.chapters[destinationDroppableId], ChangesStatus.ChildrenReorder);

		renumberChapterPages(state, state.chapters.entities[destinationDroppableId]);
	}

	return state;
};

export const deletePageFromPageGroupHelper = (state, payload) => {
	const { pageGroupId, pageId } = payload;

	const initialPagesIds = state.initialCourse.page_groups.entities[pageGroupId]?.pages || [];
	const pagesIds = state.page_groups.entities[pageGroupId].pages.filter((id) => id !== pageId);

	// remove children changes
	state.pages.entities[pageId].elements &&
		state.pages.entities[pageId].elements.forEach((elementId) => {
			if (state.changes.elements[elementId]) state.changes.elements[elementId] = [];
		});

	state.page_groups.entities[pageGroupId].pages = pagesIds;
	state.pages.entities = deleteProperty(state.pages.entities, pageId);

	const isNew = state.changes.pages[pageId]?.includes(ChangesStatus.New);

	state.changes.pages[pageId] = isNew ? [] : [ChangesStatus.Deleted];
	state.changes.page_groups[pageGroupId] = isEqualIdsArrays(initialPagesIds, pagesIds)
		? removeStatus(state.changes.page_groups[pageGroupId], ChangesStatus.ChildrenReorder)
		: addStatus(state.changes.page_groups[pageGroupId], ChangesStatus.ChildrenReorder);

	const parentChapter = getChapterByPageGroupId(state.chapters, pageGroupId);
	renumberChapterPages(state, parentChapter);

	return state;
};

export const deletePageFromChapterHelper = (state, payload) => {
	const { chapterId, pageId } = payload;

	const initialPagesIds = state.initialCourse.chapters.entities[chapterId]?.pages || [];
	const pagesIds = state.chapters.entities[chapterId].pages.filter((id) => id !== pageId);

	// remove children changes
	const elements = state.pages.entities[pageId].elements || [];
	elements.map((elementId) => {
		if (state.changes.elements[elementId]) state.changes.elements[elementId] = [];
	});

	state.chapters.entities[chapterId].pages = pagesIds;
	state.pages.entities = deleteProperty(state.pages.entities, pageId);

	const isNew = state.changes.pages[pageId]?.includes(ChangesStatus.New);

	state.changes.pages[pageId] = isNew ? [] : [ChangesStatus.Deleted];
	state.changes.chapters[chapterId] = isEqualIdsArrays(initialPagesIds, pagesIds)
		? removeStatus(state.changes.chapters[chapterId], ChangesStatus.ChildrenReorder)
		: addStatus(state.changes.chapters[chapterId], ChangesStatus.ChildrenReorder);

	renumberChapterPages(state, state.chapters.entities[chapterId]);

	return state;
};

const getPageNumber = (state, pageId) => {
	const parentChapter = getChapterByPageId(state.chapters, state.page_groups, pageId);
	const chapterPages = getChapterPages(state, parentChapter);

	const chapterIndex = state.chapters.ids.indexOf(parentChapter.family_id);
	const pageIndex = chapterPages.indexOf(pageId);

	return `Ch#${chapterIndex + 1} / Pg#${pageIndex + 1}`;
};

export const updatePageHelper = (state: CourseState, payload) => {
	const { pageId, ...page } = payload;

	const initialPage = state.initialCourse.pages.entities[pageId];
	const updatedPage = {
		...state.pages.entities[pageId],
		...page,
		...(page.page_number && {
			currentPageNumber: page.page_number || getPageNumber(state, pageId)
		})
	};

	const attributesEqual = initialPage
		? isEqual(pageToPageProperties(initialPage), pageToPageProperties(updatedPage))
		: false;

	state.pages.entities[pageId] = updatedPage;
	state.settingsMode = page.page_type ? !attributesEqual : state.settingsMode;

	state.changes.pages[pageId] = attributesEqual
		? removeStatus(state.changes.pages[pageId], ChangesStatus.Modified)
		: addStatus(state.changes.pages[pageId], ChangesStatus.Modified);

	return state;
};

export const repaginateElementsHelper = (state, payload) => {
	const { pages } = payload;

	Object.keys(pages).map((pageId) => {
		const updatedElements = pages[pageId];

		const initialElementsIds = state.initialCourse.pages.entities[pageId]?.elements || [];
		const updatedPage = { ...state.pages.entities[pageId], elements: updatedElements };

		indentElements(state, updatedPage.elements);
		state.pages.entities[pageId] = updatedPage;

		state.changes.pages[pageId] = isEqualIdsArrays(initialElementsIds, updatedPage.elements)
			? removeStatus(state.changes.pages[pageId], ChangesStatus.ChildrenReorder)
			: addStatus(state.changes.pages[pageId], ChangesStatus.ChildrenReorder);
	});

	return state;
};
